"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useState, useEffect, useRef } from "react";
import { useLocale, useTranslations } from "next-intl";
import { WorkspaceSelector } from "@/components/workspace-selector";
import Image from "next/image";
// import Link from "next/link";
import { domId } from "@/lib/dom-id";
import { UserMenuDropdown } from "./user-menu/user-menu";
import { Check, ChevronDown } from "lucide-react";
import {
  LANGUAGES,
  LOCALE_COOKIE,
  SUPPORTED_LOCALES,
  coerceLocale,
  type Locale,
} from "@/i18n/locales";
// import { getLocaleFromPathname, withLocalePath } from "@/lib/locale-routing";
import { pushWithMinimumDelay } from "@/lib/navigation";
import {
  systemStatusSelectors,
  useGlobalStore,
} from "@/store/global";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import { useShallow } from "zustand/react/shallow";

/**
 * Conditional Header Component - Oten Design
 * Only renders the Oten Connector header on specific routes
 * Hides on /specs/* routes to avoid duplicate headers
 * Hides on /welcome route for clean landing page experience
 */
export default function ConditionalHeader() {
  const [showHeader] = useGlobalStore(
    useShallow((state) => [systemStatusSelectors.showHeader(state)]),
  );
  const pathname = usePathname();
  const router = useRouter();
  const t = useTranslations("ConditionalHeader");
  const locale = useLocale();
  // const activeLocale = getLocaleFromPathname(pathname);
  const [selectedLocale, setSelectedLocale] = useState<Locale>(
    coerceLocale(locale),
  );
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const headerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setSelectedLocale(coerceLocale(locale));
  }, [locale]);
  // Hide header on spec-related pages (they have their own headers),
  // on the welcome page for a clean landing experience,
  // and when embedded in an iframe (mode=iframe)
  const shouldHideHeader =
    !showHeader ||
    (pathname?.includes("/specs/") && !pathname?.includes("/actions")) ||
    pathname?.endsWith("/welcome");

  useEffect(() => {
    if (shouldHideHeader) {
      document.documentElement.style.setProperty("--app-header-offset", "0px");
      return;
    }

    const updateHeaderOffset = () => {
      const height = headerRef.current?.offsetHeight ?? 0;
      document.documentElement.style.setProperty(
        "--app-header-offset",
        `${height}px`,
      );
    };

    updateHeaderOffset();

    const headerElement = headerRef.current;
    if (!headerElement || typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(() => {
      updateHeaderOffset();
    });
    observer.observe(headerElement);

    return () => {
      observer.disconnect();
    };
  }, [shouldHideHeader]);

  if (shouldHideHeader) {
    return null;
  }

  const selectorLocales: ReadonlyArray<Locale> = ["en", "vi"];
  const selectorLanguages = LANGUAGES.filter((lang) =>
    selectorLocales.includes(lang.locale),
  );
  const selectedLanguage =
    selectorLanguages.find((l) => l.locale === selectedLocale) ??
    selectorLanguages[0] ??
    LANGUAGES[0];
  const selectorTriggerClass =
    "flex h-10 items-center gap-2 rounded-full border border-[#B2BBC4] bg-white px-4 text-sm font-medium text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-200";

  const handleSelectLocale = (locale: Locale) => {
    setSelectedLocale(locale);
    setIsLanguageOpen(false);
    document.cookie = `${LOCALE_COOKIE}=${locale};path=/;max-age=31536000;samesite=lax`;

    const segments = (pathname || "/").split("/").filter(Boolean);
    const search = typeof window !== "undefined" ? window.location.search : "";

    if (segments.length === 0) {
      void pushWithMinimumDelay(router, `/${locale}${search}`);
      return;
    }

    const hasLocalePrefix = SUPPORTED_LOCALES.includes(segments[0] as Locale);
    if (hasLocalePrefix) {
      segments[0] = locale;
    } else {
      segments.unshift(locale);
    }

    const nextPath = `/${segments.join("/")}`;
    void pushWithMinimumDelay(router, `${nextPath}${search}`);
  };

  return (
    <header
      ref={headerRef}
      className="sticky top-0 left-0 right-0 z-50 bg-white"
    >
      <div className="flex w-full items-center justify-between px-4 py-2 border-b border-[#DCDFE3]">
        {/* Logo Section (temporarily hidden)
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link
            data-testid={domId("lnk", "header-home")}
            href={withLocalePath(activeLocale, "/")}
            className="flex items-center gap-3 text-white"
          >
            <Image
              src="/otenlogo.svg"
              alt={t("alts.logo")}
              width={208}
              height={38}
              className="h-10 w-auto"
              priority
            />
          </Link>
        </div>
        */}

        {/* Spacer */}
        <div className="flex-1"></div>

        {/* Right Section */}
        <div className="flex items-center gap-4 flex-shrink-0">
          {/* Workspace Selector - Updated styling */}
          <div className="hidden sm:block">
            <WorkspaceSelector />
          </div>

          {/* Language Selector */}
          <DropdownMenu open={isLanguageOpen} onOpenChange={setIsLanguageOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid={domId("btn", "header-language-toggle")}
                data-selector-trigger="true"
                type="button"
                variant="unstyled"
                className={selectorTriggerClass}
              >
                <Image
                  src={selectedLanguage.flagSrc}
                  alt={selectedLanguage.label}
                  width={20}
                  height={20}
                  className="flex-shrink-0"
                />
                <span className="truncate">{selectedLanguage.label}</span>
                <ChevronDown
                  className={`h-4 w-4 shrink-0 text-slate-700 transition-transform duration-150 ${
                    isLanguageOpen ? "rotate-180" : ""
                  }`}
                />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={8}
              className="w-[240px] overflow-hidden rounded-lg border-slate-200 bg-white p-1 shadow-lg"
            >
              {selectorLanguages.map((lang) => {
                const isSelected = lang.locale === selectedLocale;

                return (
                  <DropdownMenuItem
                    key={lang.locale}
                    data-testid={domId("btn", `header-language-option-${lang.locale}`)}
                    onSelect={() => handleSelectLocale(lang.locale)}
                    className={`w-full px-4 py-2.5 text-left text-sm text-slate-700 ${
                      isSelected
                        ? "bg-blue-50 text-blue-700 focus:bg-blue-50 focus:text-blue-700"
                        : ""
                    }`}
                  >
                    <div className="flex w-full items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Image
                          src={lang.flagSrc}
                          alt={lang.label}
                          width={20}
                          height={20}
                          className="flex-shrink-0"
                        />
                        <span className="font-medium leading-5">{lang.label}</span>
                      </div>
                      {isSelected && <Check className="h-5 w-5 flex-shrink-0 text-blue-600" />}
                    </div>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Logout Button - Updated styling for purple theme */}
          {/* User Avatar with Dropdown */}
          <div className="relative">
            {/* User Dropdown Menu */}
            <UserMenuDropdown />
          </div>
        </div>
      </div>
    </header>
  );
}
